import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "../components/ui/date-picker";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { 
  TrendingUp, 
  Users, 
  Calendar, 
  Activity, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  FileText,
  Download,
  Search,
  Filter
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays, isWithinInterval, startOfDay, endOfDay, parseISO, addDays, isBefore, isEqual } from "date-fns";
import { es } from "date-fns/locale";
import { toZonedTime } from 'date-fns-tz';
import { toast } from "@/components/ui/use-toast";

interface AttendanceStats {
  totalRecords: number;
  present: number;
  absent: number;
  late: number;
  justified: number;
  presentPercentage: number;
}

interface DailyStats {
  date: string;
  present: number;
  absent: number;
  late: number;
  justified: number;
}

interface ActivityStats {
  activityName: string;
  totalStudents: number;
  averageAttendance: number;
  presentCount: number;
}

interface AttendanceRecord {
  id: string;
  studentId: string;
  studentName: string;
  class: string;
  status: string;
  time: string;
  date: string;
  activityName: string;
}

interface StudentAttendance {
  id: string;
  name: string;
  class: string;
  present: number;
  absent: number;
  total: number;
  status: string;
  date: string;
  time: string;
  activityName: string;
  activityId: string;
  scheduledDays?: number[];
  requiresDaySelection: boolean;
}

const COLORS = {
  present: '#22c55e',
  absent: '#ef4444', 
  late: '#f97316',
  justified: '#3b82f6'
};

export const AttendanceStats = () => {
  const [period, setPeriod] = useState("week");
  const [selectedActivity, setSelectedActivity] = useState("all");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [stats, setStats] = useState<AttendanceStats>({
    totalRecords: 0,
    present: 0,
    absent: 0,
    late: 0,
    justified: 0,
    presentPercentage: 0
  });
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);
  const [activityStats, setActivityStats] = useState<ActivityStats[]>([]);
  const [studentAttendance, setStudentAttendance] = useState<StudentAttendance[]>([]);
  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [studentSchedules, setStudentSchedules] = useState<Record<string, number[]>>({});

  useEffect(() => {
    loadActivities();
  }, []);

  useEffect(() => {
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, startDate, endDate, selectedActivity]);

  useEffect(() => {
    // Set default period to 'today' on component mount
    setPeriod('today');
  }, []);

  const handlePeriodChange = (value: string) => {
    setPeriod(value);
    if (value !== 'custom') {
      setStartDate(null);
      setEndDate(null);
    }
  };

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
    if (date) setPeriod('custom');
  };

  const handleEndDateChange = (date: Date | null) => {
    setEndDate(date);
    if (date) setPeriod('custom');
  };

  const getDateRange = (period: string, startDate?: Date | null, endDate?: Date | null) => {
    let start: Date;
    let end: Date;
    const timeZone = 'Europe/Madrid';
    const now = toZonedTime(new Date(), timeZone);

    if (period === 'custom' && startDate && endDate) {
      start = startOfDay(startDate);
      end = endOfDay(endDate);
    } else if (period === 'today') {
      start = startOfDay(now);
      end = endOfDay(now);
    } else if (period === 'week') {
      start = startOfWeek(now, { locale: es });
      end = endOfWeek(now, { locale: es });
    } else if (period === 'month') {
      start = startOfMonth(now);
      end = endOfMonth(now);
    } else {
      // Default to last 7 days - incluir hoy y los 6 días anteriores
      start = startOfDay(subDays(now, 6));
      end = endOfDay(now);
    }

    // Convert to Madrid timezone for database query
    const madridStart = toZonedTime(start, timeZone);
    const madridEnd = toZonedTime(end, timeZone);

    const result = {
      start: format(madridStart, 'yyyy-MM-dd'),
      end: format(madridEnd, 'yyyy-MM-dd')
    };

    console.log('Date range calculation:', {
      period,
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
      now: now.toISOString(),
      start: result.start,
      end: result.end,
      timeZone,
      madridStart: madridStart.toISOString(),
      madridEnd: madridEnd.toISOString(),
      today: format(now, 'yyyy-MM-dd'),
      last7Days: period === 'last7days' ? Array.from({length: 7}, (_, i) => format(subDays(now, 6-i), 'yyyy-MM-dd')) : null
    });

    return result;
  };

  const getDatesInRange = (start: Date, end: Date): Date[] => {
    const dates: Date[] = [];
    let currentDate = start;

    while (isBefore(currentDate, end) || isEqual(currentDate, end)) {
      dates.push(currentDate);
      currentDate = addDays(currentDate, 1);
    }

    return dates;
  };

  const loadActivities = async () => {
    try {
      const { data, error } = await supabase
        .from('activities')
        .select('id, name, requires_day_selection')
        .order('name');
      
      if (error) throw error;
      setActivities(data || []);
    } catch (error) {
      console.error('Error loading activities:', error);
    }
  };

  const loadStats = async () => {
    try {
      setLoading(true);

      // Validate activity selection
      if (selectedActivity && selectedActivity !== "all") {
        const { data: activityExists } = await supabase
          .from('activities')
          .select('id')
          .eq('id', selectedActivity)
          .single();

        if (!activityExists) {
          console.error('Selected activity not found:', selectedActivity);
          toast({
            title: "Error",
            description: "La actividad seleccionada no existe",
            variant: "destructive"
          });
          return;
        }
      }

      const { start, end } = getDateRange(period, startDate, endDate);
      const startDateObj = parseISO(start);
      const endDateObj = parseISO(end);
      
      console.log('Fetching stats for date range:', { 
        start, 
        end, 
        selectedActivity,
        startDateObj: startDateObj.toISOString(),
        endDateObj: endDateObj.toISOString()
      });
      
      let query = supabase
        .from('attendances')
        .select(`
          id,
          date,
          time,
          status,
          activity_id,
          students (
            id,
            name,
            class
          ),
          activities (
            id,
            name,
            requires_day_selection
          )
        `)
        .gte('date', start)
        .lte('date', end);

      if (selectedActivity && selectedActivity !== "all") {
        query = query.eq('activity_id', selectedActivity);
      }

      const { data: attendanceData, error } = await query;

      if (error) {
        console.error('Query error:', error);
        throw error;
      }

      console.log('Query results:', { 
        start, 
        end, 
        selectedActivity, 
        attendanceData,
        recordCount: attendanceData?.length || 0,
        firstRecord: attendanceData?.[0],
        allRecords: attendanceData?.map(r => ({ 
          id: r.id, 
          date: r.date, 
          student: r.students?.name, 
          activity: r.activities?.name 
        }))
      });

      if (!attendanceData || attendanceData.length === 0) {
        console.log('No attendance data found for the date range');
        setStats({
          totalRecords: 0,
          present: 0,
          absent: 0,
          late: 0,
          justified: 0,
          presentPercentage: 0
        });
        setDailyStats([]);
        setActivityStats([]);
        setStudentAttendance([]);
        setAttendanceRecords([]);
        return;
      }

      // Process attendance records
      const records = attendanceData.map(record => ({
        id: record.id,
        studentId: record.students.id,
        studentName: record.students.name,
        class: record.students.class,
        status: record.status,
        time: record.time ? format(toZonedTime(parseISO(`${record.date}T${record.time}`), 'Europe/Madrid'), 'HH:mm:ss', { locale: es }) : '',
        date: record.date,
        activityName: record.activities.name,
        activityId: record.activity_id,
        requiresDaySelection: record.activities.requires_day_selection
      }));

      setAttendanceRecords(records);

      // Load student schedules for activities that require day selection
      await loadStudentSchedules(records.filter(r => r.requiresDaySelection));

      // Process student attendance summary - cada registro por separado
      const studentSummary = records.reduce<Record<string, StudentAttendance>>((acc, record) => {
        // Usar ID único para cada registro (no agrupar por estudiante-actividad)
        const key = `${record.id}`;
        acc[key] = {
          id: record.studentId,
          name: record.studentName,
          class: record.class,
          present: record.status === 'present' ? 1 : 0,
          absent: record.status !== 'present' ? 1 : 0,
          total: 1,
          status: record.status,
          date: record.date,
          time: record.time,
          activityName: record.activityName,
          activityId: record.activityId,
          requiresDaySelection: record.requiresDaySelection,
          scheduledDays: studentSchedules[`${record.studentId}-${record.activityId}`] || []
        };
        
        return acc;
      }, {});

      setStudentAttendance(Object.values(studentSummary));

      // Calcular estadísticas generales
      const totalRecords = records.length;
      const present = records.filter(a => a.status === 'present').length;
      const absent = records.filter(a => a.status === 'absent').length;
      const late = records.filter(a => a.status === 'late').length;
      const justified = records.filter(a => a.status === 'justified').length;
      
      setStats({
        totalRecords,
        present,
        absent,
        late,
        justified,
        presentPercentage: totalRecords > 0 ? (present / totalRecords) * 100 : 0
      });

      // Calcular estadísticas diarias
      const dailyMap = new Map<string, DailyStats>();
      const datesInRange = getDatesInRange(startDateObj, endDateObj);
      
      datesInRange.forEach(date => {
        const dateStr = format(date, 'yyyy-MM-dd');
        dailyMap.set(dateStr, {
          date: format(date, 'dd/MM', { locale: es }),
          present: 0,
          absent: 0,
          late: 0,
          justified: 0
        });
      });

      // Llenar con datos reales
      records.forEach(record => {
        const dateStr = format(parseISO(record.date), 'yyyy-MM-dd');
        const existing = dailyMap.get(dateStr);
        if (existing) {
          existing[record.status]++;
        }
      });

      setDailyStats(Array.from(dailyMap.values()));

      // Calcular estadísticas por actividad
      const activityMap = new Map<string, ActivityStats>();
      
      records.forEach(record => {
        const key = record.activityId;
        if (!activityMap.has(key)) {
          activityMap.set(key, {
            activityName: record.activityName,
            totalStudents: 0,
            averageAttendance: 0,
            presentCount: 0
          });
        }
        
        const stats = activityMap.get(key)!;
        stats.totalStudents++;
        if (record.status === 'present') {
          stats.presentCount++;
        }
      });

      // Calcular promedios
      activityMap.forEach(stats => {
        stats.averageAttendance = stats.totalStudents > 0 
          ? (stats.presentCount / stats.totalStudents) * 100 
          : 0;
      });

      setActivityStats(Array.from(activityMap.values()));

    } catch (error) {
      console.error('Error loading stats:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las estadísticas",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadStudentSchedules = async (recordsWithSchedule: any[]) => {
    try {
      const uniqueStudentActivities = recordsWithSchedule.reduce((acc: any[], record) => {
        const key = `${record.studentId}-${record.activityId}`;
        if (!acc.find(item => item.key === key)) {
          acc.push({
            key,
            studentId: record.studentId,
            activityId: record.activityId
          });
        }
        return acc;
      }, []);

      const schedules: Record<string, number[]> = {};
      
      for (const item of uniqueStudentActivities) {
        const { data: schedule } = await supabase
          .from('student_activity_schedule')
          .select('day_of_week')
          .eq('student_id', item.studentId)
          .eq('activity_id', item.activityId)
          .order('day_of_week');

        schedules[item.key] = schedule?.map(s => s.day_of_week) || [];
      }

      setStudentSchedules(schedules);
    } catch (error) {
      console.error('Error loading student schedules:', error);
    }
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { variant: "default" | "destructive" | "outline" | "secondary", label: string }> = {
      present: { variant: "default", label: "Presente" },
      absent: { variant: "destructive", label: "Ausente" },
      late: { variant: "outline", label: "Tarde" },
      justified: { variant: "secondary", label: "Justificado" }
    };

    const statusConfig = config[status] || config.absent;
    return <Badge variant={statusConfig.variant}>{statusConfig.label}</Badge>;
  };

  const exportToCSV = () => {
    const { start: startStr, end: endStr } = getDateRange(period, startDate, endDate);
    const csvStartDate = parseISO(startStr);
    const csvEndDate = parseISO(endStr);
    const dateRange = `${format(csvStartDate, 'dd-MM-yyyy')}_${format(csvEndDate, 'dd-MM-yyyy')}`;
    const filename = `asistencia_${dateRange}.csv`;

    // Create CSV content with scheduled days
    const headers = ['Fecha', 'Hora', 'Alumno', 'Clase', 'Actividad', 'Estado', 'Días Programados'];
    const csvContent = [headers.join(',')];

    filteredStudentAttendance.forEach(student => {
      const scheduledDaysText = student.requiresDaySelection && student.scheduledDays?.length 
        ? getDaysText(student.scheduledDays)
        : student.requiresDaySelection ? 'Ninguno' : 'Todos los días';
      
      const row = [
        student.date,
        student.time || '',
        student.name,
        student.class,
        student.activityName,
        student.status === 'present' ? 'Presente' : 'Ausente',
        `"${scheduledDaysText}"`
      ].join(',');
      csvContent.push(row);
    });

    // Create and download file
    const csvString = csvContent.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const getDayName = (dayNumber: number): string => {
    // Database stores ISO standard: 1=Monday, 2=Tuesday, ..., 5=Friday, 6=Saturday, 0=Sunday
    const days = ['D', 'L', 'M', 'X', 'J', 'V', 'S']; // Index 0=Sunday, 1=Monday, etc.
    return days[dayNumber] || '';
  };

  const getDaysText = (days: number[]): string => {
    // Database stores ISO standard: 1=Monday, 2=Tuesday, ..., 5=Friday, 6=Saturday, 0=Sunday
    const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']; // Index 0=Sunday, 1=Monday, etc.
    return days.map(day => dayNames[day]).join(', ');
  };

  const renderScheduledDays = (student: StudentAttendance) => {
    if (!student.requiresDaySelection) {
      return (
        <Badge variant="outline" className="text-xs">
          Todos los días
        </Badge>
      );
    }

    if (!student.scheduledDays || student.scheduledDays.length === 0) {
      return (
        <Badge variant="destructive" className="text-xs">
          Sin horario
        </Badge>
      );
    }

    return (
      <TooltipProvider>
        <UITooltip>
          <TooltipTrigger asChild>
            <div className="flex gap-1 flex-wrap">
              {[0, 1, 2, 3, 4, 5, 6].map(day => (
                <Badge
                  key={day}
                  variant={student.scheduledDays?.includes(day) ? "default" : "outline"}
                  className={`text-xs w-6 h-6 p-0 flex items-center justify-center ${
                    student.scheduledDays?.includes(day) 
                      ? 'bg-primary text-primary-foreground' 
                      : 'text-muted-foreground'
                  }`}
                >
                  {getDayName(day)}
                </Badge>
              ))}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>{getDaysText(student.scheduledDays)}</p>
          </TooltipContent>
        </UITooltip>
      </TooltipProvider>
    );
  };

  const filteredStudentAttendance = studentAttendance.filter(student => {
    const searchLower = searchTerm.toLowerCase();
    return (
      student.name.toLowerCase().includes(searchLower) ||
      student.class.toLowerCase().includes(searchLower) ||
      student.activityName.toLowerCase().includes(searchLower)
    );
  });

  const pieData = [
    { name: 'Presente', value: stats.present, color: COLORS.present },
    { name: 'Ausente', value: stats.absent, color: COLORS.absent },
    { name: 'Tardanza', value: stats.late, color: COLORS.late },
    { name: 'Justificado', value: stats.justified, color: COLORS.justified }
  ].filter(item => item.value > 0);

  if (loading) {
    return (
      <Card className="shadow-card">
        <CardContent className="text-center py-8">
          <div className="text-muted-foreground">Cargando estadísticas...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <Card className="shadow-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Estadísticas de Asistencia
            </CardTitle>
            <div className="flex gap-4">
              <Select onValueChange={handlePeriodChange} defaultValue={period}>
                <SelectTrigger className="w-40 bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-background border z-50">
                  <SelectItem value="week">Esta semana</SelectItem>
                  <SelectItem value="month">Este mes</SelectItem>
                  <SelectItem value="last7days">Últimos 7 días</SelectItem>
                  <SelectItem value="today">Hoy</SelectItem>
                  <SelectItem value="custom">Personalizado</SelectItem>
                </SelectContent>
              </Select>

              {period === 'custom' && (
                <div className="flex gap-2">
                  <DatePicker 
                    date={startDate} 
                    setDate={handleStartDateChange}
                    placeholder="Fecha inicio"
                  />
                  <DatePicker 
                    date={endDate} 
                    setDate={handleEndDateChange}
                    placeholder="Fecha fin"
                  />
                </div>
              )}

              <Select value={selectedActivity} onValueChange={setSelectedActivity}>
                <SelectTrigger className="w-48 bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-background border z-50">
                  <SelectItem value="all">Todas las actividades</SelectItem>
                  {activities.map((activity) => (
                    <SelectItem key={activity.id} value={activity.id}>
                      {activity.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button variant="outline" onClick={exportToCSV}>
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Resumen general */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="shadow-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{stats.present}</p>
                <p className="text-sm text-muted-foreground">Presentes</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{stats.absent}</p>
                <p className="text-sm text-muted-foreground">Ausentes</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-600">{stats.late}</p>
                <p className="text-sm text-muted-foreground">Tardanzas</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{stats.justified}</p>
                <p className="text-sm text-muted-foreground">Justificados</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista detallada de asistencia */}
      <Card className="shadow-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Listado de Asistencia
            </CardTitle>
            <div className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nombre, clase o actividad..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-[300px]"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px] rounded-md border">
            <div className="space-y-2 p-4">
              {filteredStudentAttendance.length > 0 ? (
                filteredStudentAttendance.map((student, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-muted rounded-lg"
                  >
                     <div className="flex items-center gap-3">
                       <Avatar>
                         <AvatarFallback>
                           {student.name.split(' ').map(n => n[0]).join('')}
                         </AvatarFallback>
                       </Avatar>
                       <div className="flex-1">
                         <h4 className="font-medium">{student.name}</h4>
                         <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                           <Badge variant="outline">{student.class}</Badge>
                           <span>•</span>
                           <span>{student.activityName}</span>
                         </div>
                         <div className="flex items-center gap-2">
                           <span className="text-xs text-muted-foreground">Días:</span>
                           {renderScheduledDays(student)}
                         </div>
                       </div>
                     </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-sm">{format(toZonedTime(parseISO(student.date), 'Europe/Madrid'), 'dd/MM/yyyy', { locale: es })}</div>
                        <div className="text-xs text-primary font-medium bg-primary/10 px-2 py-1 rounded">
                          {student.time}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Hora exacta
                        </div>
                      </div>
                      {getStatusBadge(student.status)}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No se encontraron registros
                </div>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico de barras - Evolución diaria */}
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="text-lg">Evolución Diaria</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dailyStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="present" stackId="a" fill={COLORS.present} name="Presente" />
                <Bar dataKey="late" stackId="a" fill={COLORS.late} name="Tardanza" />
                <Bar dataKey="justified" stackId="a" fill={COLORS.justified} name="Justificado" />
                <Bar dataKey="absent" stackId="a" fill={COLORS.absent} name="Ausente" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Gráfico circular - Distribución */}
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="text-lg">Distribución General</CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-20 text-muted-foreground">
                No hay datos para mostrar
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Estadísticas por actividad */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Rendimiento por Actividad
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activityStats.length > 0 ? (
            <div className="space-y-4">
              {activityStats.map((activity, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div>
                    <h4 className="font-medium">{activity.activityName}</h4>
                    <p className="text-sm text-muted-foreground">
                      {activity.totalStudents} estudiantes registrados
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right min-w-[100px]">
                      <div className="text-sm font-medium">
                        {activity.averageAttendance.toFixed(1)}%
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {activity.presentCount} presentes
                      </div>
                    </div>
                    <Progress 
                      value={activity.averageAttendance} 
                      className="w-24"
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No hay datos de actividades para mostrar
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};